import { useEffect, useRef, useState } from 'react';
import { getCourseCampus } from '@/lib/courses/group';
import { estimateHoleYardage } from '@/lib/courses/yardage';
import type { Course } from '@/lib/db/types';

const ANCC_COORDS = {
  Arlington: { lat: 38.8567, lng: -77.0625 },
  Fairfax: { lat: 38.7929, lng: -77.3050 },
} as const;

function distanceYards(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c * 1.09361);
}

interface Props {
  course?: Course | null;
  holeNum: number;
  par: number;
  teeYardageTotal?: number | null;
}

export function GpsCaddy({ course, holeNum, par, teeYardageTotal }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [permState, setPermState] = useState<'idle' | 'requesting' | 'granted' | 'denied' | 'unavailable'>('idle');
  const [position, setPosition] = useState<{ lat: number; lng: number; accuracy: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const watchIdRef = useRef<number | null>(null);

  const campus = course ? getCourseCampus(course) : 'Arlington';
  const clubCoords = ANCC_COORDS[campus === 'Fairfax' ? 'Fairfax' : 'Arlington'];
  const estimatedYardage = estimateHoleYardage(par, teeYardageTotal);

  const startTracking = () => {
    if (!navigator.geolocation) {
      setPermState('unavailable');
      setError('Geolocation not supported on this device.');
      return;
    }
    setPermState('requesting');
    setError(null);
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setPermState('granted');
        setPosition({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
      },
      (err) => {
        setPermState('denied');
        setError(err.message || 'Unable to get location.');
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 },
    );
  };

  const stopTracking = () => {
    if (watchIdRef.current != null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setPermState('idle');
    setPosition(null);
  };

  useEffect(() => () => {
    if (watchIdRef.current != null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }
  }, []);

  const distanceToClubhouse = position
    ? distanceYards(position.lat, position.lng, clubCoords.lat, clubCoords.lng)
    : null;
  const onCourse = distanceToClubhouse !== null && distanceToClubhouse < 2000;

  const statusColor = permState === 'granted'
    ? (onCourse ? 'bg-good' : 'bg-gold')
    : permState === 'denied' ? 'bg-bad' : 'bg-mute';
  const statusLabel = permState === 'granted'
    ? (onCourse ? 'ON COURSE' : 'OFF COURSE')
    : permState === 'requesting' ? 'LOCATING…'
      : permState === 'denied' ? 'PERMISSION DENIED'
        : permState === 'unavailable' ? 'UNAVAILABLE'
          : 'GPS OFF';

  return (
    <div className="mb-3 overflow-hidden rounded-xl border border-line bg-paper">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center gap-2.5 px-3.5 py-3 text-left"
      >
        <div className={`h-2 w-2 shrink-0 rounded-full ${statusColor} ${permState === 'granted' ? 'shadow-[0_0_6px_currentColor]' : ''}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-bold tracking-wide text-navy">GPS CADDY</span>
            <span className="text-[9px] font-bold tracking-wide text-mute">· {statusLabel}</span>
          </div>
          {permState === 'granted' && (
            <div className="mt-0.5 text-[11px] text-ink-soft">
              Hole {holeNum} · Par {par} · ~{estimatedYardage} yds
            </div>
          )}
          {permState === 'idle' && (
            <div className="mt-0.5 text-[11px] text-mute">Tap to enable GPS for live yardage & on-course status</div>
          )}
          {error && permState === 'denied' && (
            <div className="mt-0.5 text-[11px] text-bad">{error}</div>
          )}
        </div>
        {permState === 'idle' ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); startTracking(); }}
            className="rounded-lg bg-navy px-3 py-1.5 text-[11px] font-bold tracking-wide text-cream"
          >
            ENABLE
          </button>
        ) : permState === 'granted' ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); stopTracking(); }}
            className="rounded-lg border border-line bg-paper px-3 py-1.5 text-[11px] font-bold text-ink-soft"
          >
            OFF
          </button>
        ) : null}
      </button>
      {expanded && permState === 'granted' && position && (
        <div className="border-t border-line px-3.5 pb-3 pt-3">
          <div className="grid grid-cols-3 gap-2">
            <YardageBadge label="FRONT" value={Math.max(0, estimatedYardage - 12)} />
            <YardageBadge label="MIDDLE" value={estimatedYardage} highlight />
            <YardageBadge label="BACK" value={estimatedYardage + 14} />
          </div>
          <div className="mt-2.5 font-mono text-[10px] text-mute">
            {position.lat.toFixed(5)}°, {position.lng.toFixed(5)}° · ±{Math.round(position.accuracy)}m
          </div>
          <div className="mt-1 text-[10px] italic text-mute">
            Yardages are estimates based on par. Precise hole coordinates coming soon.
          </div>
        </div>
      )}
    </div>
  );
}

function YardageBadge({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div className={`rounded-lg px-1.5 py-2 text-center ${highlight ? 'bg-navy text-cream' : 'bg-cream text-ink'}`}>
      <div className={`text-[9px] font-bold tracking-wide ${highlight ? 'text-gold-bright' : 'text-mute'}`}>{label}</div>
      <div className="font-serif text-xl font-bold">{value}</div>
      <div className={`text-[9px] ${highlight ? 'text-gold-bright' : 'text-mute'}`}>yds</div>
    </div>
  );
}
